import { getIdToken } from 'firebase/auth'

// Every call the date game makes, in one place. The routes live on the main backend (the CLOUD
// host) — contract: promptchan-backend docs/architecture/date-game.md. Auth is the same raw
// Firebase ID token in `Authorization` that Discover's /api/get_chat_feed call sends; the feed and
// the game itself are optional-auth, so a guest call simply omits the header.
const API = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD

// Never throws. A dropped connection (a phone changing network mid-date) reports as status 0,
// the same shape as any other failure, so callers branch on `ok`/`status` and nothing rejects
// out of an event handler.
async function call(user, path, { method = 'GET', body } = {}) {
  try {
    const headers = {}
    // No Content-Type on a body-less GET: for a guest that keeps the request "simple" (no CORS
    // preflight); a signed-in call preflights for Authorization regardless.
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (user) headers.Authorization = await getIdToken(user)
    const res = await fetch(`${API}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    const data = await res.json().catch(() => ({}))
    return { ok: res.ok, status: res.status, data: data || {} }
  } catch {
    return { ok: false, status: 0, data: {} }
  }
}

const enc = encodeURIComponent

// { games: [{ character_id, name, age, title, logline, cover_url, teaser_url, imageUrl }] }
export function fetchDateGameFeed(user, limit = 12) {
  return call(user, `/api/date_game/feed?limit=${enc(limit)}`)
}

// { game, progress, character, plan_unlocked, resume_gems, safe_mode } — v2 adds chapter_locked
// (chapter 2+ for Free/guests: title/logline/cover only, no nodes) and may add `chapters`.
// `safe` asks for the safe-mode version: explicit shots arrive blacked out ({image_url: null,
// hidden: 'safe_mode'}) and the sex/milestone node as {locked: true, reason: 'safe_mode'}.
// `chapter` (v2) picks the chapter; omitted, the server serves its default (a v1 date as chapter 1).
export function fetchDateGame(
  user,
  characterId,
  { safe = false, chapter = null } = {},
) {
  const q = new URLSearchParams()
  if (chapter) q.set('chapter', String(chapter))
  if (safe) q.set('safe', '1')
  const qs = q.toString()
  return call(user, `/api/date_game/${enc(characterId)}${qs ? `?${qs}` : ''}`)
}

// Fire-and-forget: progress must never block play, and a failed post only costs the server a
// record the next one (idempotent) or the local mirror covers. Guests have no progress to post.
export function postDateProgress(user, characterId, event) {
  if (!user || !event) return
  call(user, `/api/date_game/${enc(characterId)}/progress`, {
    method: 'POST',
    body: event,
  })
}

// 200 { ok, gems, node_id } | 402 { error, gems } | 400 not a checkpoint | 403 not reached.
// `key` is the idempotency key: reuse it when retrying the same resume so a flaky network cannot
// charge twice.
// v2 adds `chapter` to the body; v1 sends exactly what it did.
export function resumeDateGame(user, characterId, checkpointId, key, chapter) {
  const body = { checkpoint_id: checkpointId, key }
  if (chapter) body.chapter = chapter
  return call(user, `/api/date_game/${enc(characterId)}/resume`, {
    method: 'POST',
    body,
  })
}

export function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID)
      return crypto.randomUUID()
  } catch {
    /* fall through */
  }
  return `dg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
